/** Score, combo, lives and the persisted best for the endless run. Pure, immutable updates. */

export const SCORING = {
  /** Points per metre travelled (100 px). */
  meter: 1,
  pxPerMeter: 100,
  bone: 10,
  golden: 50,
  /** A bone collected within this window of the previous one extends the combo. */
  comboWindowMs: 1500,
  /** Every this many combo bones raise the multiplier by one. */
  comboPerStep: 4,
  maxMultiplier: 5,
  /** Level completion bonus = levelBonus × (level index + 1). */
  levelBonus: 500,
  /** Stomping a rolling thorn (also counts toward the combo). */
  stomp: 30,
  startLives: 3,
  maxLives: 5,
} as const;

export interface ScoreState {
  score: number;
  /** Where the run started (px); metres count from here. */
  startXPx: number;
  /** Furthest x reached (px). */
  maxXPx: number;
  bones: number;
  combo: number;
  comboLeftMs: number;
  multiplier: number;
  lives: number;
  levelsCompleted: number;
}

export interface BestRecord {
  score: number;
  distanceM: number;
  level: number;
}

/** A fresh run; distance counts from `startXPx` (the spawn), not from world x 0. */
export function createScore(lives: number = SCORING.startLives, startXPx = 0): ScoreState {
  return {
    score: 0,
    startXPx,
    maxXPx: startXPx,
    bones: 0,
    combo: 0,
    comboLeftMs: 0,
    multiplier: 1,
    lives,
    levelsCompleted: 0,
  };
}

export function distanceM(s: ScoreState): number {
  return Math.floor((s.maxXPx - s.startXPx) / SCORING.pxPerMeter);
}

/** Advances timers and awards distance for new ground covered. */
export function tickScore(s: ScoreState, dtMs: number, xPx: number): ScoreState {
  const maxXPx = Math.max(s.maxXPx, xPx);
  const gained =
    Math.floor(maxXPx / SCORING.pxPerMeter) - Math.floor(s.maxXPx / SCORING.pxPerMeter);
  const comboLeftMs = Math.max(0, s.comboLeftMs - dtMs);
  const expired = comboLeftMs === 0 && s.comboLeftMs > 0;
  return {
    ...s,
    maxXPx,
    score: s.score + gained * SCORING.meter,
    comboLeftMs,
    combo: expired ? 0 : s.combo,
    multiplier: expired ? 1 : s.multiplier,
  };
}

export function multiplierFor(combo: number): number {
  return Math.min(SCORING.maxMultiplier, 1 + Math.floor(combo / SCORING.comboPerStep));
}

export function collect(s: ScoreState, kind: 'bone' | 'golden'): ScoreState {
  const combo = s.combo + 1;
  const multiplier = multiplierFor(combo);
  const base = kind === 'golden' ? SCORING.golden : SCORING.bone;
  return {
    ...s,
    bones: s.bones + 1,
    combo,
    multiplier,
    comboLeftMs: SCORING.comboWindowMs,
    score: s.score + base * multiplier,
  };
}

export function stomp(s: ScoreState): ScoreState {
  const combo = s.combo + 1;
  const multiplier = multiplierFor(combo);
  return {
    ...s,
    combo,
    multiplier,
    comboLeftMs: SCORING.comboWindowMs,
    score: s.score + SCORING.stomp * multiplier,
  };
}

export function completeLevel(s: ScoreState, levelIndex: number): ScoreState {
  return {
    ...s,
    levelsCompleted: s.levelsCompleted + 1,
    score: s.score + SCORING.levelBonus * (levelIndex + 1),
    lives: Math.min(SCORING.maxLives, s.lives + 1),
  };
}

export function loseLife(s: ScoreState): ScoreState {
  return { ...s, lives: Math.max(0, s.lives - 1), combo: 0, comboLeftMs: 0, multiplier: 1 };
}

export function isGameOver(s: ScoreState): boolean {
  return s.lives <= 0;
}

export function toRecord(s: ScoreState, levelIndex: number): BestRecord {
  return { score: s.score, distanceM: distanceM(s), level: levelIndex + 1 };
}

/** The better of two records (by score, then distance). */
export function betterOf(a: BestRecord | null, b: BestRecord | null): BestRecord | null {
  if (!a) return b;
  if (!b) return a;
  if (b.score !== a.score) return b.score > a.score ? b : a;
  return b.distanceM > a.distanceM ? b : a;
}

export function parseRecord(raw: unknown): BestRecord | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null);
  const score = num(r.score);
  const distance = num(r.distanceM);
  const level = num(r.level);
  if (score === null || distance === null || level === null) return null;
  return { score, distanceM: distance, level };
}
